/* ── Is a tour of mine still running? ─────────────────────────────────
   One answer, used by two things that must never disagree: the pill in
   the corner of every page, and the Guided Tour button on a project page
   that has to refuse to start a second room.

   ── WHY NOT THE ACTIVE CLAIM ──
   The project page already keeps a claim at `guidedRoom:active:v2:<host>`,
   written when a room is created. It is the fast path and it stays. But
   it is ONE key written by ONE browser: start a tour on the laptop, open
   the project on the desktop, and the claim is simply absent — the page
   offers to create a room, and the backend answers 409 with a sentence
   that names no project and links to nothing. The host is told they are
   blocked by something they cannot see.

   The host credential is a second, independent trace of the same fact —
   `guidedRoom:host:v1:<roomId>`, written for every room this browser
   hosted. Neither key is authority. The ROOM is, which is why every
   candidate here is confirmed against it before it is believed.

   ── WHY IT CLEANS UP ──
   A room that has ENDED, or that no longer exists, takes its key with it.
   Otherwise every stale credential costs a request on every page load,
   forever. Anything else — a timeout, a 5xx, an expired token — is this
   request's problem and not the room's, so the key stays and the next
   call tries again. */
import { getGuidedRoom } from "rc-app/api/guidedRoomActionsApi";
import { isRoomGone, isRoomLive, HOST_CRED_KEY } from "rc-app/utils/guidedRoomFlow";

export interface LiveHostedRoom {
  room_id: string;
  /* SCHEDULED | LOBBY_OPEN | LIVE — all three block a new room, but only
     the last two are a tour actually happening, which is a different
     sentence to say to the host. */
  status: string;
  join_code: string;
  project_id: string;
  project_name: string;
}

/** Room ids this browser has hosted, from the host credentials left behind. */
export const hostedRoomIds = (): string[] => {
  const ids: string[] = [];
  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const k = localStorage.key(i);
      if (k && k.startsWith(`${HOST_CRED_KEY}:`)) {
        const id = k.slice(HOST_CRED_KEY.length + 1).trim();
        if (id) ids.push(id);
      }
    }
  } catch { /* storage blocked — then there is nothing to find */ }
  return ids;
};

const forgetHostCred = (roomId: string): void => {
  try { localStorage.removeItem(`${HOST_CRED_KEY}:${roomId}`); } catch { /* ignore */ }
};

const asLive = (roomId: string, r: any, status: string): LiveHostedRoom => ({
  room_id: roomId,
  status,
  join_code: String(r?.join_code || r?.room?.join_code || ""),
  project_id: String(r?.project_id || r?.room?.project_id || ""),
  project_name: String(r?.project_name || r?.room?.project_name || "").trim(),
});

/** Every room this browser hosted that the server still calls LIVE. */
export const findLiveHostedRooms = async (): Promise<LiveHostedRoom[]> => {
  const ids = hostedRoomIds();
  if (!ids.length) return [];
  const results = await Promise.all(ids.map(async (roomId) => {
    try {
      const r: any = await getGuidedRoom(roomId);
      /* The blocking set is SCHEDULED + LOBBY_OPEN + LIVE, not LIVE alone:
         the backend frees a project only on ENDED/CANCELLED/EXPIRED. A
         room that has just been created sits in LOBBY_OPEN until the tour
         starts, so looking for "LIVE" made exactly the room most likely to
         be in the way the one nobody could see. Same helper the rest of
         the app uses, so the two can never disagree again. */
      if (isRoomLive(r)) return asLive(roomId, r, String(r?.status || r?.state || "").toUpperCase());
      forgetHostCred(roomId);   // ENDED, CANCELLED, EXPIRED — it owns nothing now
      return null;
    } catch (e: any) {
      if (isRoomGone(e?.response?.status)) forgetHostCred(roomId);
      return null;
    }
  }));
  return results.filter(Boolean) as LiveHostedRoom[];
};

/** The first live one, which is all the one-room-per-host rule allows. */
export const findLiveHostedRoom = async (): Promise<LiveHostedRoom | null> =>
  (await findLiveHostedRooms())[0] || null;
